<template>
  <Dropdown
    :input-id="inputId"
    :placeholder="placeholder"
    :options="options"
  >
    <template
      #default="{
        options,
        value,
        displayValue,
        hasValue,
        hasError,
        errors,
        showClearButton,
        clearText,
        onChange: onFieldChange,
        onClear: onFieldClear,
      }"
    >
      <Listbox
        v-slot="{ open }"
        :disabled="disabled"
        @update:model-value="onChange($event, onFieldChange)"
      >
        <ListboxLabel
          v-if="label"
          class="vp-label"
        >
          {{ label }}
        </ListboxLabel>
        <ListboxButton
          class="vnf-dropdown-button"
          :class="{ open, 'has-value': hasValue }"
        >
          <button
            v-if="showClearButton"
            type="button"
            class="vnf-dropdown-clear-btn"
            :title="clearText"
            @click.prevent.stop="onClear(onFieldClear)"
          >
            <Close />
          </button>

          <div class="vnf-dropdown-value">
            {{ displayValue }}
          </div>
        </ListboxButton>

        <ListboxOptions class="vnf-dropdown-options">
          <ListboxOption
            v-for="opt in options"
            :key="opt"
            :value="opt.value"
            class="vnf-dropdown-option"
          >
            {{ opt.name }}
            <Check
              v-if="value === opt.value"
              class="checkmark"
            />
          </ListboxOption>
        </ListboxOptions>
      </Listbox>
      <span
        v-if="hasError"
        class="text-vp-error font-bold text-xs"
      >{{ errors.join(', ') }}</span>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { Dropdown } from 'vuniform';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption, ListboxLabel } from '@headlessui/vue';
import { Close, Check } from 'mdue';

const emit = defineEmits<{
  (e: 'change', value?: unknown): void;
  (e: 'clear'): void;
}>();

interface Props {
  inputId: string;
  options?: { value: string; name: string }[];
  defaultValue?: string;
  clearable?: boolean;
  errors?: string[];
  forceError?: boolean;
  multiple?: boolean;
  placeholder?: string;
  clearText?: string;
  overrideValue?: string;
  label?: string;
  disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  clearText: 'Clear',
  options: () => [],
  errors: () => [],
});

const onChange = (e: unknown, cb: (value?: unknown) => void) => {
  if (cb) {
    cb(e);
  }
  emit('change', e);
};

const onClear = (cb: () => void) => {
  if (cb) {
    cb();
  }
  emit('clear');
};
</script>
